// SIMONE-APPROVAL-REOFFER-PENDING-CLIENT-CALL-UI-1 — the browser half of the
// durable client-tool suspension.
//
// When the cloud loop needs a platform action run in the user's own UI it
// SUSPENDS and waits for a tool-result POST. The live copies of that pending
// call — the approval promise in `simoneAiTransport._pendingToolApprovals` and
// the `tool.call_required` SSE frame — both die with the page, so a reload used
// to strand the turn forever. The server side
// (SIMONE-APPROVAL-STRANDS-SERVER-LOOP-ON-RELOAD-1) now persists the whole call
// on the conversation as `metadata.pendingClientCall`
// ({ callId, tool, fn, args, confirm, suspendedAt }) and serves it on the
// ordinary conversation read; `.../tool-result` already accepts that callId
// from a brand-new connection.
//
// This module is the policy that turns that served record back into an offer,
// and drives the run/POST/resume round-trip. Pure + injected on purpose: the
// approval gate, the platform runner, the POST and the answer sink all arrive
// as functions, so the whole decision is unit-testable with no browser, no SDK
// and no DOM — the same shape `threadPointer.js` uses.

// A resumed turn can suspend again on the NEXT client tool (ask_user →
// follow-on write is the measured chain). Bounded so a server that keeps
// re-suspending can never spin the page.
export const MAX_RESUME_CHAIN = 8

// The transcript's own proof that a callId is already settled. The server
// writes exactly one `workspace_client_action` message per resolved call — the
// real result, or the honest "NOT run" close a later user message appends — and
// the conversation serializer passes message metadata through. Re-offering a
// call that already carries one would ask the user to approve something the
// server has closed; the POST would answer `duplicate:true` and the row would
// lie in the meantime.
export const isClientCallResolved = (messages, callId) => {
  const id = String(callId || '')
  if (!id) return true
  const list = Array.isArray(messages) ? messages : []
  return list.some(
    (m) =>
      m &&
      m.metadata &&
      m.metadata.kind === 'workspace_client_action' &&
      String(m.metadata.callId || '') === id
  )
}

// Normalize the served record into the exact shape the live `onToolCall` path
// hands the authorization gate: { callId, tool, fn, args, confirm, readOnly }. Anything
// that cannot be COMPLETED is not an offer — without a callId there is nothing
// to POST, and without an fn there is nothing to run — so those degrade to
// null rather than painting an approval card that can never settle.
export const normalizePendingClientCall = (pending) => {
  if (!pending || typeof pending !== 'object') return null
  const callId = String(pending.callId || '')
  const fn = String(pending.fn || '')
  if (!callId || !fn) return null
  return {
    callId,
    tool: String(pending.tool || fn),
    fn,
    // The server resolved the positional args at suspend time (`tool.toArgs`).
    // A non-array is a malformed record, not an empty call — pass [] rather
    // than spreading a string into character arguments.
    args: Array.isArray(pending.args) ? pending.args : [],
    confirm: !!pending.confirm,
    // MODULE-FN-READONLY-ENFORCED-1 — the server marks an app verb read-only;
    // the gate still checks the live stash before skipping the ask.
    readOnly: !!pending.readOnly,
    suspendedAt: pending.suspendedAt || null
  }
}

// The whole read-side decision, from one conversation envelope
// ({ conversation, messages }) as `sdk.ai.getConversation` returns it.
export const pendingClientCallOffer = (conversation, messages) => {
  const offer = normalizePendingClientCall(
    conversation && conversation.metadata && conversation.metadata.pendingClientCall
  )
  if (!offer) return null
  if (isClientCallResolved(messages, offer.callId)) return null
  return offer
}

// THE ACCEPTED RESUME (REOFFER-RESUMED-ANSWER-NEVER-PAINTED-1). A resume the
// server cannot finish inside its ack window (TURN_ACK_TIMEOUT_MS, 8 s) answers
// 202 `{ accepted: true }` and keeps the loop running; the answer then rides
// the conversation's SSE stream, which a restored or re-opened thread does not
// follow. Measured on dev 2026-10-05 (conversation 6ac35133…65db): the
// re-offered finRunway ran at 12:49:03, the model answered at 12:49:12, and the
// thread never showed the answer until a reload. `turnSettledAfter` reads ONE
// ordinary conversation read ({ conversation, messages }) for the end of the
// turn the call resumed:
//   { suspended: offer } — the loop paused again on a NEW client call;
//   { done: true }       — after the call's recorded result, an assistant
//                          message with no tool call: the answer;
//   null                 — still running.
const _hasToolCall = (m) =>
  Array.isArray(m && m.content) && m.content.some((p) => p && p.type === 'tool_call')
export const turnSettledAfter = (data, callId) => {
  const id = String(callId || '')
  const next = pendingClientCallOffer(data && data.conversation, data && data.messages)
  if (next && next.callId !== id) return { suspended: next }
  const list = Array.isArray(data && data.messages) ? data.messages : []
  const at = list.findIndex(
    (m) =>
      m &&
      m.metadata &&
      m.metadata.kind === 'workspace_client_action' &&
      String(m.metadata.callId || '') === id
  )
  if (at < 0) return null
  const answered = list.slice(at + 1).some((m) => m && m.role === 'assistant' && !_hasToolCall(m))
  return answered ? { done: true } : null
}

// Drive one re-offered call to completion, then follow the resume envelope.
//
// `runWithAuth(call)` — the SAME authorization gate the live turn uses. In
//   "Ask first" mode an effectful call parks on its timeline row until the user
//   answers Run / Skip; a read-only or navigation tool, and Auto mode, run
//   straight through (that is the ticket's "confirm:false render/navigation
//   tools may re-run directly", expressed as ONE policy instead of a second
//   copy of it). A server `confirm: true` forces the ask regardless of mode —
//   the caller passes that through as the gate's mode override.
// `submitToolResult(callId, result)` — POST the outcome. The server resolves a
//   callId exactly once, so a late or duplicate POST is acknowledged, never
//   re-run; that is what makes the retry below safe.
// `onAnswer(assistantMessage)` — the final answer the resume envelope carries
//   when the loop finishes instead of suspending again.
// `awaitTurn(callId)` — for an ACCEPTED resume (202, see turnSettledAfter):
//   follows the turn until it lands and resolves `{ done: true }`, the NEXT
//   suspension `{ suspended: offer }` (re-offered here, like an envelope's), or
//   null when it gave up. Optional — without it an accepted resume just ends.
//
// Never throws: a resume that fails must leave an honest row, not an unhandled
// rejection inside a restore.
export const resumeFromPendingClientCall = async ({
  offer,
  runWithAuth,
  submitToolResult,
  onAnswer,
  awaitTurn,
  maxChain = MAX_RESUME_CHAIN
} = {}) => {
  let call = normalizePendingClientCall(offer)
  if (!call) return { ok: false, ran: 0, error: 'No completable pending call.' }
  if (typeof runWithAuth !== 'function' || typeof submitToolResult !== 'function') {
    return { ok: false, ran: 0, error: 'No runner or submitter for the pending call.' }
  }

  let ran = 0
  for (let step = 0; step < Math.max(1, maxChain); step++) {
    let result
    try {
      result = await runWithAuth(call)
    } catch (err) {
      // The tool itself threw. The loop still needs an outcome — reporting the
      // failure resumes it honestly; saying nothing strands it exactly as the
      // reload did.
      result = { ok: false, error: (err && err.message) || String(err) }
    }
    // A new message superseded the step while it waited on Run / Skip: the
    // server closes the call under that message (closeDanglingClientCall,
    // reason `new_message`), so nothing is posted — a POST here would race the
    // new message for the conversation's turn lock.
    if (result && result.superseded) return { ok: true, ran, superseded: true }
    ran += 1

    let resume
    try {
      resume = await submitToolResult(call.callId, result === undefined ? { ok: true } : result)
    } catch (err) {
      return { ok: false, ran, error: (err && err.message) || String(err) }
    }
    if (!resume) return { ok: true, ran }
    if (resume.duplicate) return { ok: true, ran, duplicate: true }
    if (resume.suspended) {
      const next = normalizePendingClientCall(resume)
      if (!next) return { ok: true, ran, suspended: true }
      call = next
      continue
    }
    if (resume.accepted) {
      if (typeof awaitTurn !== 'function') return { ok: true, ran, accepted: true }
      let settled = null
      try {
        settled = await awaitTurn(call.callId)
      } catch (_) {
        settled = null
      }
      if (settled && settled.suspended) {
        const next = normalizePendingClientCall(settled.suspended)
        if (!next) return { ok: true, ran, suspended: true }
        call = next
        continue
      }
      return { ok: true, ran, accepted: true, settled: !!(settled && settled.done) }
    }
    if (resume.assistantMessage && typeof onAnswer === 'function') {
      try {
        onAnswer(resume.assistantMessage)
      } catch (_) {
        /* painting the answer is best-effort — the turn already resumed */
      }
      return { ok: true, ran, answered: true }
    }
    return { ok: true, ran }
  }
  return { ok: true, ran, chainCapped: true }
}
